// Compass configuration — which spokes each of the two ASTRA ring systems
// (SHAPE life-areas, ORDER gears) shows. Every built-in spoke can be toggled
// off, and the user can append custom spokes per system. Persisted in
// user_settings.oracle_sector_config (jsonb). The ORACLE tool's editor AND
// the live ASTRA / SyQi compass both derive their spoke lists from here, so any
// change made in one place is mirrored everywhere.
//
// STAGE is NOT a ring system here — it's the chronological layer of ORDER
// (Step→Today→Aim→Goal→Endeavour), rendered as CompassView's concentric
// S/T/A/G/E orbits within the ORDER (and SHAPE) discs. See CompassView's
// ORB_STAGES.
import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { SECTORS, SECTOR_LABELS, type Sector } from '../domain/olmps';
import type { GearId } from './ascension';
import type { DmtToolId } from './spectrum-tools';
import { dayKeyOf, type DeityProgress } from './deity-progress';
import { legacyL1Id } from './deity-data';
import { SECTOR_COLOR } from '../v10/theme';
import { DEFAULT_GAME_SETTINGS, EMPTY_DAY, normalizeGameSettings, normalizeLedger, type DayLedger, type GameSettings, type PointsLedger } from './gameSettings';

export type RingSystemId = 'order' | 'shape';
export const RING_SYSTEMS: RingSystemId[] = ['order', 'shape'];
export const RING_SYSTEM_LABEL: Record<RingSystemId, string> = {
  order: 'ORDER',
  shape: 'SHAPE',
};

/** A user-defined extra spoke. Has no built-in data key, so it carries no
 *  drift / project data yet — it renders as a plain spoke in its own colour. */
export interface CustomSpoke {
  id: string; // 'shape-custom-<n>' etc.
  label: string;
  color: string; // #rrggbb
}

/** Per-system on/off + custom spokes. */
export interface SystemConfig {
  disabled: string[]; // built-in spoke ids turned off
  custom: CustomSpoke[];
  /** User renames of built-in spokes (spoke id → label). Absent = default name. */
  labels?: Record<string, string>;
}

/** One user-defined malady→deity swap on the MoyRi SWAPS surface. Independently
 *  toggleable, unlike acquisition (which is the default-on surface above it). */
export interface CustomSwap {
  id: string; // 'swap-<n>-<slug>'
  maladyKey: string; // `${toolId}:${maladyId}`
  deityToolId: DmtToolId;
  enabled: boolean;
}

export interface CompassConfig {
  systems: { order: SystemConfig; shape: SystemConfig };
  /** SHAPE ring off until unlocked (ASTRA acquired) or manually switched on. */
  shapeEnabled: boolean;
  /** Per-gear SWAP toggle (legacy MoyRi surface) — retained for back-compat;
   *  the new SWAPS section uses `swaps` instead. */
  swapEnabled: Partial<Record<GearId, boolean>>;
  /** Per-deity current acquisition tier index (0=STEP,1=TODAY,2=AIM,3=GOAL). */
  acquisitionTier: Partial<Record<DmtToolId, number>>;
  /** Per-deity chosen step option — LEGACY single-pick, migrated into
   *  `principleProgress.picks` on load; retained only for back-compat. */
  stepChoice: Partial<Record<DmtToolId, number>>;
  /** Per-deity acquisition progress: active principle picks + completion log.
   *  The completion log is the source of truth for the point engine. */
  principleProgress: Partial<Record<DmtToolId, DeityProgress>>;
  /** User-defined malady→deity swaps for the SWAPS section. */
  swaps: CustomSwap[];
  /** Per-user game rules (session times, week shape, windows, green bands). */
  game: GameSettings;
  /** Per-day award marks — the one points engine's source (gameSettings.ts). */
  ledger: PointsLedger;
}

const EMPTY_SYSTEM: SystemConfig = { disabled: [], custom: [] };
export const DEFAULT_COMPASS_CONFIG: CompassConfig = {
  systems: {
    order: { disabled: [], custom: [] },
    shape: { disabled: [], custom: [] },
  },
  shapeEnabled: true, // SHAPE is never locked (Update Directive 1.2)
  swapEnabled: {},
  acquisitionTier: {},
  stepChoice: {},
  principleProgress: {},
  swaps: [],
  game: DEFAULT_GAME_SETTINGS,
  ledger: {},
};

/** Built-in sector fill colours — mirrors CompassView's SECTOR_FILL. */
export const SECTOR_FILL: Record<Sector, string> = {
  S: SECTOR_COLOR.S, // Social — orange
  H: SECTOR_COLOR.H, // Health — red
  A: SECTOR_COLOR.A, // Admin — purple
  P: SECTOR_COLOR.P, // Personal — blue
  E: SECTOR_COLOR.E, // Economic — green
};

/** Planetary glyph prefixed to each built-in SHAPE sector label. */
export const SECTOR_GLYPH: Record<Sector, string> = {
  A: '♄', // Admin — Saturn
  E: '♃', // Economic — Jupiter
  H: '♂', // Health — Mars
  S: '♀', // Social — Venus
  P: '☿', // Personal — Mercury
};

export const CUSTOM_SECTOR_COLORS = ['#0EA5A5', '#D9488B', '#8B5CF6', '#F59E0B', '#3B82F6', '#84CC16'];
export const CUSTOM_SECTOR_GLYPH = '✦';

/** One resolved spoke, built-in or custom, ready to draw. `key` is the built-in
 *  Sector slot for data lookups (drift / colour), or null for a custom spoke. */
export interface Spoke {
  id: string;
  key: Sector | null;
  label: string;
  color: string;
  glyph: string;
  builtin: boolean;
  /** SOLO compass: this spoke IS one active TKNE project (its id). */
  projectId?: string;
}

// Built-in spokes per system. ORDER reuses the five sector colour slots (in
// S/H/A/P/E order) so its disc looks identical to before — only the labels
// differ.
const SHAPE_BUILTINS: Spoke[] = SECTORS.map((s) => ({
  id: s,
  key: s,
  label: SECTOR_LABELS[s],
  color: SECTOR_FILL[s],
  glyph: SECTOR_GLYPH[s],
  builtin: true,
}));

const ORDER_LABELS_ORDERED = ['Orient', 'Regulate', 'Direct', 'Execute', 'Revise'];

const ORDER_BUILTINS: Spoke[] = SECTORS.map((s, i) => ({
  id: `order-${ORDER_LABELS_ORDERED[i].toLowerCase()}`,
  key: s,
  label: ORDER_LABELS_ORDERED[i],
  color: SECTOR_FILL[s],
  glyph: '',
  builtin: true,
}));

export function builtinSpokes(system: RingSystemId): Spoke[] {
  return system === 'shape' ? SHAPE_BUILTINS : ORDER_BUILTINS;
}

function normalizeSystem(raw: unknown): SystemConfig {
  const r = (raw ?? {}) as Partial<SystemConfig>;
  return {
    disabled: Array.isArray(r.disabled) ? r.disabled : [],
    custom: Array.isArray(r.custom) ? r.custom : [],
    labels: r.labels && typeof r.labels === 'object' ? (r.labels as Record<string, string>) : {},
  };
}

function normalizeSwapEnabled(raw: unknown): Partial<Record<GearId, boolean>> {
  if (!raw || typeof raw !== 'object') return {};
  const out: Partial<Record<GearId, boolean>> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'boolean') out[k as GearId] = v;
  }
  return out;
}

function normalizeAcquisitionTier(raw: unknown): Partial<Record<DmtToolId, number>> {
  if (!raw || typeof raw !== 'object') return {};
  const out: Partial<Record<DmtToolId, number>> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    // 0..4 (4 = all four tiers complete / secured).
    if (typeof v === 'number' && Number.isFinite(v)) out[k as DmtToolId] = Math.max(0, Math.min(4, Math.round(v)));
  }
  return out;
}

function normalizeStepChoice(raw: unknown): Partial<Record<DmtToolId, number>> {
  if (!raw || typeof raw !== 'object') return {};
  const out: Partial<Record<DmtToolId, number>> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'number' && Number.isFinite(v)) out[k as DmtToolId] = Math.max(0, Math.min(9, Math.round(v)));
  }
  return out;
}

/** Normalize per-deity progress. Picks and the log key by stable principle id
 *  (string); legacy numeric indices are migrated to their L1 id, and a legacy
 *  single `stepChoice` seeds picks where no progress exists yet. */
function normalizeProgress(
  raw: unknown,
  stepChoice: Partial<Record<DmtToolId, number>>,
): Partial<Record<DmtToolId, DeityProgress>> {
  const out: Partial<Record<DmtToolId, DeityProgress>> = {};
  // Accept a stored value as either a string id or a legacy numeric L1 index.
  const toId = (toolId: string, v: unknown): string | null => {
    if (typeof v === 'string' && v.length) return v;
    if (typeof v === 'number' && Number.isFinite(v)) return legacyL1Id(toolId, Math.round(v));
    return null;
  };
  if (raw && typeof raw === 'object') {
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
      if (!v || typeof v !== 'object') continue;
      const p = v as { picks?: unknown; log?: unknown; startedOn?: unknown };
      const picks = Array.isArray(p.picks)
        ? (p.picks.map((x) => toId(k, x)).filter((x): x is string => !!x))
        : [];
      const log: Record<string, string[]> = {};
      if (p.log && typeof p.log === 'object') {
        for (const [d, arr] of Object.entries(p.log as Record<string, unknown>)) {
          if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || !Array.isArray(arr)) continue;
          const ids = arr.map((x) => toId(k, x)).filter((x): x is string => !!x);
          if (ids.length) log[d] = Array.from(new Set(ids));
        }
      }
      const next: DeityProgress = { picks: Array.from(new Set(picks)), log };
      // startedOn: the day the deity became active (its daily requirement, and so its seat marks, run from here).
      if (typeof p.startedOn === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(p.startedOn)) next.startedOn = p.startedOn;
      out[k as DmtToolId] = next;
    }
  }
  // Migration: seed picks from a legacy stepChoice where no progress exists yet.
  for (const [k, idx] of Object.entries(stepChoice)) {
    if (!out[k as DmtToolId] && typeof idx === 'number') {
      out[k as DmtToolId] = { picks: [legacyL1Id(k, idx)], log: {} };
    }
  }
  return out;
}

function normalizeSwaps(raw: unknown): CustomSwap[] {
  if (!Array.isArray(raw)) return [];
  const out: CustomSwap[] = [];
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue;
    const s = r as Partial<CustomSwap>;
    if (typeof s.id !== 'string' || typeof s.maladyKey !== 'string' || typeof s.deityToolId !== 'string') continue;
    out.push({
      id: s.id,
      maladyKey: s.maladyKey,
      deityToolId: s.deityToolId as DmtToolId,
      enabled: typeof s.enabled === 'boolean' ? s.enabled : true,
    });
  }
  return out;
}

/** Accepts the stored jsonb in any historical shape and returns a full config.
 *  Tolerates old persisted shapes: a legacy 'stage' system key is dropped
 *  silently; unknown/forward-compat keys are carried through (spread first,
 *  known keys normalized on top) so a round-trip never loses data. */
export function normalizeConfig(raw: unknown): CompassConfig {
  if (!raw || typeof raw !== 'object') return DEFAULT_COMPASS_CONFIG;
  const r = raw as Record<string, unknown>;

  // Current shape: `{ systems: { order, shape }, shapeEnabled, swapEnabled }`.
  if (r.systems && typeof r.systems === 'object') {
    const s = r.systems as Record<string, unknown>;
    return {
      ...r,
      systems: {
        order: normalizeSystem(s.order),
        shape: normalizeSystem(s.shape),
      },
      shapeEnabled: true, // stored false no longer locks SHAPE
      swapEnabled: normalizeSwapEnabled(r.swapEnabled),
      acquisitionTier: normalizeAcquisitionTier(r.acquisitionTier),
      stepChoice: normalizeStepChoice(r.stepChoice),
      principleProgress: normalizeProgress(r.principleProgress, normalizeStepChoice(r.stepChoice)),
      swaps: normalizeSwaps(r.swaps),
      game: normalizeGameSettings(r.game),
      ledger: normalizeLedger(r.ledger),
    } as CompassConfig;
  }

  // Legacy per-system flat shape `{ shape, stage, order }` — 'stage' is
  // dropped silently (it's no longer a ring system). Destructure the legacy
  // top-level keys out so they don't get re-spread into the normalized
  // object (they would otherwise persist forever in the jsonb).
  if ('shape' in r || 'stage' in r || 'order' in r) {
    const { order: _legacyOrder, shape: _legacyShape, stage: _legacyStage, ...rest } = r;
    return {
      ...rest,
      systems: {
        order: normalizeSystem(r.order),
        shape: normalizeSystem(r.shape),
      },
      shapeEnabled: true, // stored false no longer locks SHAPE
      swapEnabled: normalizeSwapEnabled(r.swapEnabled),
      acquisitionTier: normalizeAcquisitionTier(r.acquisitionTier),
      stepChoice: normalizeStepChoice(r.stepChoice),
      principleProgress: normalizeProgress(r.principleProgress, normalizeStepChoice(r.stepChoice)),
      swaps: normalizeSwaps(r.swaps),
      game: normalizeGameSettings(r.game),
      ledger: normalizeLedger(r.ledger),
    } as CompassConfig;
  }

  // Legacy SHAPE-only shape `{ disabled, custom }`.
  if ('disabled' in r || 'custom' in r) {
    return {
      systems: { order: EMPTY_SYSTEM, shape: normalizeSystem(r) },
      shapeEnabled: true,
      swapEnabled: {},
      acquisitionTier: {},
      stepChoice: {},
      principleProgress: {},
      swaps: [],
      game: DEFAULT_GAME_SETTINGS,
      ledger: {},
    };
  }

  return DEFAULT_COMPASS_CONFIG;
}

/** The ordered spoke list a system's disc draws: enabled built-ins followed by
 *  custom spokes. Never empty — falls back to the full built-in set. */
export function activeSpokes(config: CompassConfig | null | undefined, system: RingSystemId): Spoke[] {
  const sc = config ? config.systems[system] : EMPTY_SYSTEM;
  const builtins = builtinSpokes(system)
    .filter((b) => !sc.disabled.includes(b.id))
    .map((b) => (sc.labels?.[b.id] ? { ...b, label: sc.labels[b.id] } : b));
  const custom: Spoke[] = sc.custom.map((c) => ({
    id: c.id,
    key: null,
    label: c.label,
    color: c.color,
    glyph: system === 'shape' ? CUSTOM_SECTOR_GLYPH : '',
    builtin: false,
  }));
  const all = [...builtins, ...custom];
  return all.length ? all : builtinSpokes(system);
}

// Every screen mounts its own useCompassConfig copy. After any copy saves, the
// others re-read so a later write (e.g. the evening session's markDay) never
// clobbers a setting changed elsewhere with a stale snapshot.
const configSavedListeners = new Set<(origin: object) => void>();

/** Load / persist user_settings.oracle_sector_config with per-system helpers. */
export function useCompassConfig(userId: string | undefined): {
  config: CompassConfig;
  loading: boolean;
  spokesFor: (system: RingSystemId) => Spoke[];
  isEnabled: (system: RingSystemId, id: string) => boolean;
  toggleBuiltin: (system: RingSystemId, id: string) => void;
  addCustom: (system: RingSystemId, label: string) => void;
  renameCustom: (system: RingSystemId, id: string, label: string) => void;
  /** Rename any spoke — built-in (stored as a label override) or custom. Empty restores the default. */
  renameSpoke: (system: RingSystemId, id: string, label: string) => void;
  removeCustom: (system: RingSystemId, id: string) => void;
  setShapeEnabled: (enabled: boolean) => void;
  setSwapEnabled: (gear: GearId, enabled: boolean) => void;
  setAcquisitionTier: (toolId: DmtToolId, idx: number) => void;
  setStepChoice: (toolId: DmtToolId, idx: number) => void;
  /** Add/remove a principle (by stable id) from a deity's active picks. */
  togglePrinciple: (toolId: DmtToolId, id: string) => void;
  /** Log/unlog a completion of one principle (by id) for `dateKey` (toggles). */
  logPrincipleToday: (toolId: DmtToolId, id: string, dateKey: string) => void;
  addSwap: (maladyKey: string, deityToolId: DmtToolId) => void;
  updateSwap: (id: string, patch: Partial<Omit<CustomSwap, 'id'>>) => void;
  removeSwap: (id: string) => void;
  /** Save the per-user game rules (Settings → Game). */
  setGameSettings: (next: GameSettings) => void;
  /** Merge award marks into one day's ledger entry (dateKey YYYY-MM-DD). */
  markDay: (dateKey: string, patch: Partial<DayLedger>) => void;
} {
  const [config, setConfig] = useState<CompassConfig>(DEFAULT_COMPASS_CONFIG);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from('user_settings')
      .select('oracle_sector_config')
      .eq('user_id', userId)
      .maybeSingle();
    setConfig(normalizeConfig((data as { oracle_sector_config?: unknown } | null)?.oracle_sector_config));
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const origin = useRef({}).current;
  useEffect(() => {
    const fn = (from: object) => {
      if (from !== origin) void load();
    };
    configSavedListeners.add(fn);
    return () => {
      configSavedListeners.delete(fn);
    };
  }, [load, origin]);

  // NOTE: supabase-js v2 query builders are LAZY — the HTTP request only fires
  // when the builder is awaited or `.then()`-ed. A bare `void supabase…update()`
  // builds the query but never sends it, so nothing persists. We MUST call
  // `.then()` here (and surface errors) for the write to actually happen.
  const writeConfig = useCallback(
    (next: CompassConfig) => {
      if (!userId) return;
      void supabase
        .from('user_settings')
        .update({ oracle_sector_config: next })
        .eq('user_id', userId)
        .then(({ error }) => {
          if (error) console.warn('[sectorConfig] save failed:', error.message);
          else configSavedListeners.forEach((fn) => fn(origin));
        });
    },
    [userId, origin]
  );

  const patchConfig = useCallback(
    (patch: Partial<CompassConfig>) => {
      const next: CompassConfig = { ...config, ...patch };
      setConfig(next);
      writeConfig(next);
    },
    [config, writeConfig]
  );

  const patchSystem = useCallback(
    (system: RingSystemId, patch: Partial<SystemConfig>) => {
      const next: CompassConfig = {
        ...config,
        systems: { ...config.systems, [system]: { ...config.systems[system], ...patch } },
      };
      setConfig(next);
      writeConfig(next);
    },
    [config, writeConfig]
  );

  const isEnabled = useCallback(
    (system: RingSystemId, id: string) => !config.systems[system].disabled.includes(id),
    [config]
  );

  const toggleBuiltin = useCallback(
    (system: RingSystemId, id: string) => {
      const disabled = config.systems[system].disabled;
      patchSystem(system, {
        disabled: disabled.includes(id) ? disabled.filter((d) => d !== id) : [...disabled, id],
      });
    },
    [config, patchSystem]
  );

  const addCustom = useCallback(
    (system: RingSystemId, label: string) => {
      const name = label.trim();
      if (!name) return;
      const list = config.systems[system].custom;
      const n = list.length;
      const id = `${system}-custom-${n}-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
      const color = CUSTOM_SECTOR_COLORS[n % CUSTOM_SECTOR_COLORS.length];
      patchSystem(system, { custom: [...list, { id, label: name, color }] });
    },
    [config, patchSystem]
  );

  const renameCustom = useCallback(
    (system: RingSystemId, id: string, label: string) => {
      patchSystem(system, {
        custom: config.systems[system].custom.map((c) => (c.id === id ? { ...c, label } : c)),
      });
    },
    [config, patchSystem]
  );

  const renameSpoke = useCallback(
    (system: RingSystemId, id: string, label: string) => {
      const sc = config.systems[system];
      if (sc.custom.some((c) => c.id === id)) {
        patchSystem(system, { custom: sc.custom.map((c) => (c.id === id ? { ...c, label } : c)) });
        return;
      }
      const labels = { ...(sc.labels ?? {}) };
      if (label.trim()) labels[id] = label;
      else delete labels[id];
      patchSystem(system, { labels });
    },
    [config, patchSystem]
  );

  const removeCustom = useCallback(
    (system: RingSystemId, id: string) => {
      patchSystem(system, { custom: config.systems[system].custom.filter((c) => c.id !== id) });
    },
    [config, patchSystem]
  );

  const setShapeEnabled = useCallback(
    (enabled: boolean) => patchConfig({ shapeEnabled: enabled }),
    [patchConfig]
  );

  const setSwapEnabled = useCallback(
    (gear: GearId, enabled: boolean) => patchConfig({ swapEnabled: { ...config.swapEnabled, [gear]: enabled } }),
    [config.swapEnabled, patchConfig]
  );

  const setAcquisitionTier = useCallback(
    (toolId: DmtToolId, idx: number) =>
      patchConfig({
        acquisitionTier: { ...config.acquisitionTier, [toolId]: Math.max(0, Math.min(4, Math.round(idx))) },
      }),
    [config.acquisitionTier, patchConfig]
  );

  const setStepChoice = useCallback(
    (toolId: DmtToolId, idx: number) =>
      patchConfig({ stepChoice: { ...config.stepChoice, [toolId]: Math.max(0, Math.min(9, Math.round(idx))) } }),
    [config.stepChoice, patchConfig]
  );

  const togglePrinciple = useCallback(
    (toolId: DmtToolId, id: string) => {
      const prev = config.principleProgress[toolId] ?? { picks: [], log: {} };
      const has = prev.picks.includes(id);
      const picks = has ? prev.picks.filter((p) => p !== id) : [...prev.picks, id];
      // First pick ever (empty → non-empty) starts the deity's clock — local 'YYYY-MM-DD'.
      const startedOn = prev.startedOn ?? (prev.picks.length === 0 && picks.length > 0 ? dayKeyOf(new Date()) : undefined);
      patchConfig({
        principleProgress: { ...config.principleProgress, [toolId]: { ...prev, picks, ...(startedOn ? { startedOn } : {}) } },
      });
    },
    [config.principleProgress, patchConfig]
  );

  const logPrincipleToday = useCallback(
    (toolId: DmtToolId, id: string, dateKey: string) => {
      const prev = config.principleProgress[toolId] ?? { picks: [], log: {} };
      const day = prev.log[dateKey] ?? [];
      const has = day.includes(id);
      const nextDay = has ? day.filter((i) => i !== id) : [...day, id];
      const log = { ...prev.log };
      if (nextDay.length) log[dateKey] = nextDay;
      else delete log[dateKey];
      patchConfig({
        principleProgress: { ...config.principleProgress, [toolId]: { ...prev, log } },
      });
    },
    [config.principleProgress, patchConfig]
  );

  const addSwap = useCallback(
    (maladyKey: string, deityToolId: DmtToolId) => {
      const n = config.swaps.length;
      const id = `swap-${n}-${maladyKey.replace(/[^a-z0-9]+/gi, '-')}`;
      patchConfig({ swaps: [...config.swaps, { id, maladyKey, deityToolId, enabled: true }] });
    },
    [config.swaps, patchConfig]
  );

  const updateSwap = useCallback(
    (id: string, patch: Partial<Omit<CustomSwap, 'id'>>) =>
      patchConfig({ swaps: config.swaps.map((s) => (s.id === id ? { ...s, ...patch } : s)) }),
    [config.swaps, patchConfig]
  );

  const removeSwap = useCallback(
    (id: string) => patchConfig({ swaps: config.swaps.filter((s) => s.id !== id) }),
    [config.swaps, patchConfig]
  );

  const setGameSettings = useCallback((next: GameSettings) => patchConfig({ game: next }), [patchConfig]);
  const markDay = useCallback(
    (dateKey: string, patch: Partial<DayLedger>) =>
      patchConfig({ ledger: { ...config.ledger, [dateKey]: { ...EMPTY_DAY, ...config.ledger[dateKey], ...patch } } }),
    [config.ledger, patchConfig]
  );

  const spokesFor = useCallback((system: RingSystemId) => activeSpokes(config, system), [config]);

  return {
    config,
    loading,
    spokesFor,
    isEnabled,
    toggleBuiltin,
    addCustom,
    renameCustom,
    renameSpoke,
    removeCustom,
    setShapeEnabled,
    setSwapEnabled,
    setAcquisitionTier,
    setStepChoice,
    togglePrinciple,
    logPrincipleToday,
    addSwap,
    updateSwap,
    removeSwap,
    setGameSettings,
    markDay,
  };
}
